import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { CheckCircle, Calendar, Download, ChevronRight, ChevronLeft } from "lucide-react";
import { toast } from "sonner";

const CALENDARS = [
  {
    id: 'google',
    name: 'Google Calendar',
    icon: '📅',
    color: 'from-blue-500 to-blue-600',
    steps: [
      '1. Descarga el archivo .ics haciendo clic en el botón de abajo',
      '2. Abre Google Calendar (calendar.google.com)',
      '3. En el menú lateral izquierdo, haz clic en el "+" junto a "Otros calendarios"',
      '4. Selecciona "Importar"',
      '5. Haz clic en "Seleccionar archivo de tu ordenador"',
      '6. Elige el archivo .ics que descargaste',
      '7. Selecciona el calendario donde quieres importarlo',
      '8. Haz clic en "Importar"',
      '¡Listo! Tu evento aparecerá en Google Calendar'
    ]
  },
  {
    id: 'outlook',
    name: 'Outlook / Microsoft 365',
    icon: '📆',
    color: 'from-blue-600 to-blue-700',
    steps: [
      '1. Descarga el archivo .ics haciendo clic en el botón de abajo',
      '2. Abre Outlook Web (outlook.com) o la aplicación de escritorio',
      '3. Ve a tu calendario',
      '4. Haz clic en "Agregar calendario" en la parte superior',
      '5. Selecciona "Cargar desde archivo"',
      '6. Haz clic en "Examinar" y selecciona el archivo .ics',
      '7. Elige el calendario de destino',
      '8. Haz clic en "Importar"',
      '¡Tu evento se habrá agregado a Outlook!'
    ]
  },
  {
    id: 'apple',
    name: 'Apple Calendar',
    icon: '🍎',
    color: 'from-gray-700 to-gray-800',
    steps: [
      '1. Descarga el archivo .ics haciendo clic en el botón de abajo',
      '2. En tu Mac, abre la aplicación "Calendario"',
      '3. Ve al menú "Archivo" → "Importar"',
      '4. Selecciona el archivo .ics que descargaste',
      '5. Elige el calendario donde deseas agregarlo',
      '6. Haz clic en "Aceptar"',
      'En iPhone/iPad: Toca el archivo .ics descargado y selecciona "Agregar al Calendario"',
      '¡El evento se sincronizará automáticamente con iCloud!'
    ]
  },
  {
    id: 'other',
    name: 'Otro Calendario',
    icon: '📋',
    color: 'from-gray-600 to-gray-700',
    steps: [
      '1. Descarga el archivo .ics haciendo clic en el botón de abajo',
      '2. Abre tu aplicación de calendario preferida',
      '3. Busca la opción "Importar" o "Agregar evento desde archivo"',
      '4. Selecciona el archivo .ics descargado',
      '5. El evento se importará automáticamente',
      'Nota: La mayoría de los calendarios modernos soportan el formato .ics estándar'
    ]
  }
];

export default function SyncCalendarModal({ isOpen, onClose, event }) {
  const [step, setStep] = useState(1); // 1: Select Calendar, 2: Instructions
  const [selectedCalendar, setSelectedCalendar] = useState(null);

  const handleSelectCalendar = (calendar) => {
    setSelectedCalendar(calendar);
    setStep(2);
  };

  const handleDownloadICS = () => {
    if (!event) {
      toast.error("No hay evento seleccionado");
      return;
    }

    const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
    const token = localStorage.getItem("token");
    
    // Open download in new tab
    const url = `${API}/calendar/events/${event.id}/export-ics`;
    
    // Use fetch to download with auth
    fetch(url, {
      headers: { Authorization: `Bearer ${token}` }
    })
      .then(response => response.blob())
      .then(blob => {
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${event.title}.ics`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
        toast.success("Archivo .ics descargado. Ahora sigue las instrucciones.");
      })
      .catch(error => {
        console.error("Error downloading ICS:", error);
        toast.error("Error al descargar el archivo");
      });
  };

  const handleBack = () => {
    if (step === 2) {
      setStep(1);
      setSelectedCalendar(null);
    }
  };

  const handleClose = () => {
    setStep(1);
    setSelectedCalendar(null);
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[700px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-2xl font-bold flex items-center gap-2">
            <Download className="text-orange-500" />
            Sincronizar con mi Calendario
          </DialogTitle>
        </DialogHeader>

        {/* Step 1: Select Calendar */}
        {step === 1 && (
          <div className="space-y-6 mt-6">
            <div className="text-center">
              <p className="text-gray-600 mb-6">
                Elige tu aplicación de calendario preferida:
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {CALENDARS.map((calendar) => (
                <button
                  key={calendar.id}
                  onClick={() => handleSelectCalendar(calendar)}
                  className={`
                    relative overflow-hidden rounded-xl p-6 
                    bg-gradient-to-br ${calendar.color}
                    text-white hover:scale-105 transition-all duration-200
                    shadow-lg hover:shadow-xl
                    flex flex-col items-center gap-3
                    group
                  `}
                >
                  <div className="text-5xl">{calendar.icon}</div>
                  <h3 className="font-bold text-lg">{calendar.name}</h3>
                  <ChevronRight 
                    className="absolute right-4 top-1/2 -translate-y-1/2 opacity-0 group-hover:opacity-100 transition-opacity" 
                    size={24} 
                  />
                </button>
              ))}
            </div>

            <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <p className="text-sm text-blue-800">
                💡 <strong>Consejo:</strong> Selecciona el calendario que uses habitualmente. 
                Te guiaremos paso a paso para importar tu evento.
              </p>
            </div>
          </div>
        )}

        {/* Step 2: Instructions */}
        {step === 2 && selectedCalendar && (
          <div className="space-y-6 mt-6">
            {/* Header with calendar info */}
            <div className={`
              rounded-xl p-4 bg-gradient-to-br ${selectedCalendar.color}
              text-white flex items-center gap-4
            `}>
              <div className="text-4xl">{selectedCalendar.icon}</div>
              <div>
                <h3 className="font-bold text-lg">{selectedCalendar.name}</h3>
                <p className="text-sm opacity-90">
                  Sigue estos pasos para importar tu evento
                </p>
              </div>
            </div>

            {/* Download button */}
            <div className="flex justify-center">
              <Button
                onClick={handleDownloadICS}
                size="lg"
                className="bg-orange-500 hover:bg-orange-600 text-white gap-2"
              >
                <Download size={20} />
                Descargar archivo .ics
              </Button>
            </div>

            {/* Instructions */}
            <div className="bg-gray-50 rounded-xl p-6 space-y-4">
              <h4 className="font-bold text-lg mb-4 flex items-center gap-2">
                <CheckCircle className="text-green-500" />
                Instrucciones paso a paso:
              </h4>

              <ol className="space-y-3">
                {selectedCalendar.steps.map((step, index) => (
                  <li key={index} className="flex gap-3">
                    <span className="flex-shrink-0 w-6 h-6 rounded-full bg-orange-500 text-white text-xs flex items-center justify-center font-bold mt-0.5">
                      {index + 1}
                    </span>
                    <span className="text-sm text-gray-700">{step}</span>
                  </li>
                ))}
              </ol>
            </div>

            {/* Help section */}
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
              <p className="text-sm text-yellow-800">
                <strong>¿Necesitas más ayuda?</strong> Visita nuestra{' '}
                <a href="/ayuda/calendario" className="underline font-semibold hover:text-yellow-900" target="_blank" rel="noopener noreferrer">
                  guía completa de calendario
                </a>
                {' '}con instrucciones detalladas paso a paso.
              </p>
            </div>

            {/* Navigation buttons */}
            <div className="flex justify-between pt-4">
              <Button
                variant="outline"
                onClick={handleBack}
                className="gap-2"
              >
                <ChevronLeft size={16} />
                Volver
              </Button>
              <Button
                onClick={handleClose}
                className="bg-green-600 hover:bg-green-700"
              >
                ¡Entendido!
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
